import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Plane, Upload, QrCode, ShieldCheck, ArrowRight, Clock, Search } from "lucide-react";
import PublicShell from "@/components/public/PublicShell";

/**
 * VAULT · Travel — public product page (/product/travel).
 *
 * Positioning (per spec):
 *   VAULT is NOT an airline. VAULT does NOT issue tickets. VAULT
 *   organises the customer's own airline boarding pass so it's easy
 *   to find at the airport.
 *
 * Rules honoured here:
 *   §3  — Synthetic demo data only (`MAA → DEL · Sample Airline · VA123`).
 *   §5  — Airport Mode capability is LIVE — included honestly.
 *   §6  — PNR Fetch is currently BLOCKED on Amadeus keys — the page
 *          copy says "if you have the booking reference" but does NOT
 *          claim universal PNR retrieval.
 *   §10 — Uses the approved brand lines. Zero AI slop phrases.
 *   §11 — Apple / Google Wallet chips render `Coming with your issuer`
 *          because certs aren't provisioned; never advertised LIVE.
 *   §15 — No fake personalised travel alerts.
 */

export default function ProductTravel() {
    return (
        <PublicShell>
            <Helmet>
                <title>Travel & Boarding Passes · VAULT by PD</title>
                <meta name="description" content="Keep your airline boarding pass and flight details organised in VAULT. Airport Mode brings your boarding pass to the front — one tap, ready for scanning." />
                <link rel="canonical" href="https://www.vaultbypd.com/product/travel" />
            </Helmet>

            <div className="bg-white text-stone-900" data-testid="product-travel-page">
                {/* ─── HERO ─── */}
                <section className="max-w-6xl mx-auto px-5 sm:px-8 pt-14 sm:pt-24 pb-12 sm:pb-20 grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
                    <div>
                        <div className="text-[11px] font-bold uppercase tracking-widest text-amber-700 mb-3">TRAVEL</div>
                        <h1 className="font-display text-4xl sm:text-5xl leading-[1.05] text-stone-900">
                            Travel, organised.
                        </h1>
                        <p className="text-base text-stone-600 mt-5 max-w-md">
                            Keep your flight details and boarding pass close &mdash; especially when you actually need them.
                        </p>
                        <div className="mt-6 flex flex-wrap gap-3" data-testid="travel-hero-ctas">
                            <Link to="/signup"
                                className="inline-flex items-center gap-1.5 px-5 py-3 rounded-full bg-amber-400 hover:bg-amber-300 text-stone-900 text-[13.5px] font-semibold">
                                Create Your VAULT <ArrowRight size={13} />
                            </Link>
                            <Link to="/demo/personal?view=travel"
                                data-testid="travel-hero-demo-cta"
                                className="inline-flex items-center gap-1.5 px-5 py-3 rounded-full border border-stone-300 hover:border-stone-500 text-[13.5px] font-semibold text-stone-800">
                                Try the demo
                            </Link>
                        </div>
                        <p className="text-[12px] text-stone-500 mt-4">
                            VAULT organises your airline boarding pass. It never issues tickets or generates airline credentials.
                        </p>
                    </div>

                    {/* Mobile-first Boarding Pass mock — SYNTHETIC DEMO ONLY (spec §3) */}
                    <BoardingPassMock />
                </section>

                {/* ─── SECTION 1 — Add your boarding pass ─── */}
                <FeatureRow
                    eyebrow="ADD"
                    headline="Add your boarding pass."
                    body="Upload the PDF from your airline, scan a printed copy with your phone, or type the flight details manually. VAULT keeps them together with the rest of your travel."
                    icon={Upload}
                    tone="cobalt"
                    testId="travel-section-add"
                >
                    <BadgeRow items={["Upload PDF or image", "Scan boarding pass", "Enter manually"]} />
                </FeatureRow>

                {/* ─── SECTION 2 — Upcoming flight ─── */}
                <FeatureRow
                    reverse
                    eyebrow="UPCOMING"
                    headline="Your upcoming flight, easy to find."
                    body="VAULT surfaces your next flight without making you search through folders, emails and downloads. When it's flight day, the boarding pass is where you expect it."
                    icon={Clock}
                    tone="emerald"
                    testId="travel-section-upcoming"
                >
                    <BadgeRow items={["Trip organiser", "Upcoming travel card", "Global search by flight or city"]} />
                </FeatureRow>

                {/* ─── SECTION 3 — Airport Mode ─── */}
                <FeatureRow
                    eyebrow="AIRPORT MODE"
                    headline="One tap. Boarding pass ready."
                    body="When your flight is approaching, VAULT can bring your saved boarding pass to the front &mdash; with the airline&rsquo;s barcode or QR code ready for scanning, in a clean full-screen view."
                    icon={QrCode}
                    tone="amber"
                    testId="travel-section-airport-mode"
                >
                    <BadgeRow items={["Full-screen scanning surface", "Screen wake while open", "Passenger & segment selectors"]} />
                    <p className="text-[12px] text-stone-500 mt-3 max-w-md">
                        Airport Mode displays the boarding pass you saved &mdash; the same credential you would present from your airline. VAULT never generates a boarding pass from a booking reference alone.
                    </p>
                </FeatureRow>

                {/* ─── SECTION 4 — Wallet (spec §11 · not LIVE yet) ─── */}
                <section className="bg-stone-50 border-y border-stone-200" data-testid="travel-section-wallet">
                    <div className="max-w-6xl mx-auto px-5 sm:px-8 py-14 grid grid-cols-1 md:grid-cols-2 gap-10">
                        <div>
                            <div className="text-[11px] font-bold uppercase tracking-widest text-stone-500 mb-3">WALLET</div>
                            <h2 className="font-display text-3xl text-stone-900">Wallet access, where genuinely configured.</h2>
                            <p className="text-[14px] text-stone-600 mt-4 max-w-md">
                                Once VAULT&rsquo;s signing credentials are provisioned with Apple and Google, you&rsquo;ll be able to add your boarding pass to Apple Wallet and Google Wallet. Until then, we don&rsquo;t pretend the feature is live.
                            </p>
                        </div>
                        <div className="flex flex-wrap items-center gap-3" data-testid="travel-wallet-chips">
                            <span className="inline-flex items-center gap-2 px-3 py-2 rounded-full border border-stone-300 bg-white text-[12.5px] text-stone-700">
                                Apple Wallet · <span className="text-stone-500">setup in progress</span>
                            </span>
                            <span className="inline-flex items-center gap-2 px-3 py-2 rounded-full border border-stone-300 bg-white text-[12.5px] text-stone-700">
                                Google Wallet · <span className="text-stone-500">setup in progress</span>
                            </span>
                        </div>
                    </div>
                </section>

                {/* ─── SECTION 5 — Privacy ─── */}
                <FeatureRow
                    reverse
                    eyebrow="PRIVACY"
                    headline="Your travel stays private by default."
                    body="Boarding passes belong to the traveller. Sharing with your partner or family is always explicit &mdash; nothing appears in someone else&rsquo;s VAULT unless you send it there."
                    icon={ShieldCheck}
                    tone="teal"
                    testId="travel-section-privacy"
                >
                    <BadgeRow items={["Per-passenger", "Together / Family sharing is opt-in", "Tenant-isolated storage"]} />
                </FeatureRow>

                {/* ─── FINAL CTA ─── */}
                <section className="bg-stone-900 text-stone-100" data-testid="travel-final-cta">
                    <div className="max-w-4xl mx-auto px-5 sm:px-8 py-16 text-center">
                        <div className="text-[11px] font-bold uppercase tracking-widest text-amber-300 mb-3">GET STARTED</div>
                        <h2 className="font-display text-3xl sm:text-4xl leading-tight">
                            Keep your next journey in VAULT.
                        </h2>
                        <p className="text-[14px] text-stone-400 mt-4 max-w-lg mx-auto">
                            Less searching at the airport. Your flight details, where you can actually find them.
                        </p>
                        <div className="mt-7 flex flex-wrap justify-center gap-3">
                            <Link to="/signup"
                                className="inline-flex items-center gap-1.5 px-5 py-3 rounded-full bg-amber-400 hover:bg-amber-300 text-stone-900 text-[13.5px] font-semibold">
                                Create Your VAULT <ArrowRight size={13} />
                            </Link>
                            <Link to="/product"
                                className="inline-flex items-center gap-1.5 px-5 py-3 rounded-full border border-stone-700 hover:border-stone-500 text-[13.5px] font-semibold text-stone-100">
                                All product capabilities
                            </Link>
                        </div>
                    </div>
                </section>
            </div>
        </PublicShell>
    );
}

// ─── Building blocks ─────────────────────────────────────────

function FeatureRow({ eyebrow, headline, body, icon: Icon, tone = "amber", reverse, children, testId }) {
    const dot = {
        amber: "bg-amber-100 text-amber-700",
        cobalt: "bg-sky-100 text-sky-700",
        emerald: "bg-emerald-100 text-emerald-700",
        teal: "bg-teal-100 text-teal-700",
    }[tone] || "bg-stone-100 text-stone-700";
    return (
        <section className="max-w-6xl mx-auto px-5 sm:px-8 py-14" data-testid={testId}>
            <div className={`grid grid-cols-1 md:grid-cols-2 gap-10 items-start ${reverse ? "md:[&>*:first-child]:order-2" : ""}`}>
                <div>
                    <div className="text-[11px] font-bold uppercase tracking-widest text-stone-500 mb-3">{eyebrow}</div>
                    <h2 className="font-display text-3xl text-stone-900">{headline}</h2>
                    <p className="text-[14px] text-stone-600 mt-4 max-w-md" dangerouslySetInnerHTML={{ __html: body }} />
                    <div className="mt-5">{children}</div>
                </div>
                <div className="flex items-center justify-center">
                    <div className={`w-24 h-24 rounded-2xl ${dot} flex items-center justify-center`}>
                        <Icon size={36} />
                    </div>
                </div>
            </div>
        </section>
    );
}

function BadgeRow({ items }) {
    return (
        <div className="flex flex-wrap gap-2">
            {items.map((label) => (
                <span key={label} className="inline-flex items-center px-3 py-1.5 rounded-full border border-stone-200 bg-white text-[12.5px] text-stone-700">
                    {label}
                </span>
            ))}
        </div>
    );
}

// SYNTHETIC DEMO — spec §3 · never real passenger data.
function BoardingPassMock() {
    return (
        <div className="mx-auto max-w-[340px] w-full" data-testid="travel-hero-mock" aria-hidden="true">
            <div className="relative rounded-3xl bg-white border border-stone-200 shadow-xl overflow-hidden">
                <div className="px-5 pt-5 pb-4 border-b border-dashed border-stone-200">
                    <div className="flex items-center gap-2">
                        <Plane size={14} className="text-amber-600" />
                        <div className="text-[10px] font-bold uppercase tracking-widest text-amber-700">Sample Airline · VA123</div>
                        <span className="ml-auto text-[9.5px] font-semibold uppercase tracking-widest text-stone-400">DEMO</span>
                    </div>
                    <div className="text-4xl font-semibold text-stone-900 tabular-nums mt-2">
                        MAA <span className="text-stone-400 mx-2">→</span> DEL
                    </div>
                    <div className="text-[12px] text-stone-500 mt-1">Sample flight · 08:10 boarding</div>
                </div>
                <div className="grid grid-cols-3 gap-2 p-4 text-center">
                    <div>
                        <div className="text-[10px] font-bold uppercase tracking-widest text-stone-500">Boarding</div>
                        <div className="text-lg font-semibold tabular-nums">08:10</div>
                    </div>
                    <div>
                        <div className="text-[10px] font-bold uppercase tracking-widest text-stone-500">Gate</div>
                        <div className="text-lg font-semibold tabular-nums">12</div>
                    </div>
                    <div>
                        <div className="text-[10px] font-bold uppercase tracking-widest text-stone-500">Seat</div>
                        <div className="text-lg font-semibold tabular-nums">4A</div>
                    </div>
                </div>
                {/* Placeholder scan zone — NOT a real barcode. Spec §3 forbids fake credentials. */}
                <div className="mx-4 mb-4 rounded-xl bg-stone-100 border border-dashed border-stone-300 p-4 text-center">
                    <div className="text-[10px] font-semibold uppercase tracking-widest text-stone-500 mb-2">Airport Mode</div>
                    <div className="text-[11px] text-stone-500">
                        The airline&rsquo;s actual boarding barcode appears here at the airport.
                    </div>
                </div>
                <div className="px-5 pb-5">
                    <div className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-full bg-amber-400 text-stone-900 text-[13px] font-semibold">
                        <QrCode size={13} /> Show Boarding Pass
                    </div>
                </div>
            </div>
        </div>
    );
}
